import { setupFormatter } from "../../../components/scenes/Layers/fields/formatters";
import { isFilledArray } from "../../../utils";
import { portableRegexSearch } from "../../../utils/portableRegex";
import { getFilledArrayOrDefault } from "../../../utils/getFilledArrayOrDefault";
import { FIELD } from "../../Layers/fields";

// * Helper function to check if a value is set (not null, undefined, empty, false, or NaN)
const attributeIsSet = (attribute) => attribute !== null && attribute !== undefined && attribute !== "" && attribute !== false && !Number.isNaN(attribute);

// * Helper: detect fieldname_getters that hold date values, so generic fields
// * (CUSTOM_LAYER_META, CUSTOM_RIPENING_ROOM_META, etc.) can auto-format them.
// * Strict word-boundary match — must be `date`, `*_date*`, or `date_*` so
// * non-date words like `candidate`, `mandate`, `update`, `validate` are not
// * caught. Catches all known date fieldname_getters: pack_date, harvest_date,
// * arrival_date_intake, estimated_date_of_arrival, departure_date,
// * starting_date, packing_date, ripening_start_date, ripening_end_date.
// * `formatDate` itself is safe for non-date values — dayjs returns the raw
// * value unchanged when parsing fails — but we still keep this regex narrow.
const DATE_FIELD_PATTERN = /(?:^|_)date(?:$|_)/i;
const looksLikeDateField = (fieldname_getter: string | undefined): boolean => !!fieldname_getter && DATE_FIELD_PATTERN.test(fieldname_getter);

// * Helper function to check if meta_conditions match
// * Returns true if all meta_conditions are satisfied
function matchesMetaConditions(meta_conditions, layer, check = {}, fruit = {}) {
    if (!meta_conditions || !Array.isArray(meta_conditions)) {
        return true;
    }

    const meta_location_object = {
        layer_meta: layer || {},
        check_meta: check || {},
        fruit_meta: fruit || {},
        parent_meta: layer?.parent_meta || {}
    };

    return meta_conditions.every((meta) => {
        // * Skip if not setup correctly
        if (!meta.field || !meta.meta_location) {
            return true;
        }

        // Get value with automatic fallback for layer_meta: check layer first, then parent_meta
        let value;
        if (meta.meta_location === "layer_meta") {
            // Check layer first, then automatically fallback to parent_meta (like LayerMetaField does)
            value = meta_location_object.layer_meta?.[meta.field];
            if (!attributeIsSet(value) && meta_location_object.parent_meta) {
                value = meta_location_object.parent_meta[meta.field];
            }
        } else {
            // For check_meta and fruit_meta, use direct lookup
            if (!meta_location_object[meta.meta_location]) {
                return true;
            }
            value = meta_location_object[meta.meta_location][meta.field];
        }

        // If value is not set and we have conditions, fail the match
        if (!attributeIsSet(value) && (
            attributeIsSet(meta.gte)
            || attributeIsSet(meta.lt)
            || attributeIsSet(meta.gt)
            || attributeIsSet(meta.lte)
            || attributeIsSet(meta.eq)
            || attributeIsSet(meta.starts_with)
            || attributeIsSet(meta.ends_with)
            || attributeIsSet(meta.regex)
        )) {
            return false;
        }

        // Check numeric operators
        if (attributeIsSet(meta.gte)) {
            try {
                if (parseFloat(value) < parseFloat(meta.gte)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }

        if (attributeIsSet(meta.lt)) {
            try {
                if (parseFloat(value) >= parseFloat(meta.lt)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }

        if (attributeIsSet(meta.gt)) {
            try {
                if (parseFloat(value) <= parseFloat(meta.gt)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }

        if (attributeIsSet(meta.lte)) {
            try {
                if (parseFloat(value) > parseFloat(meta.lte)) {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }

        // Check string equality
        if (attributeIsSet(meta.eq)) {
            if (String(value) !== String(meta.eq)) {
                return false;
            }
        }

        // Check substring matching (starts with / ends with and their negations)
        if (attributeIsSet(meta.starts_with)) {
            if (!String(value).startsWith(String(meta.starts_with))) {
                return false;
            }
        }
        if (attributeIsSet(meta.ends_with)) {
            if (!String(value).endsWith(String(meta.ends_with))) {
                return false;
            }
        }
        // The not_* conditions pass when the value is missing (it does not start/end with anything)
        if (attributeIsSet(meta.not_starts_with) && attributeIsSet(value)) {
            if (String(value).startsWith(String(meta.not_starts_with))) {
                return false;
            }
        }
        if (attributeIsSet(meta.not_ends_with) && attributeIsSet(value)) {
            if (String(value).endsWith(String(meta.not_ends_with))) {
                return false;
            }
        }

        // Regex (portable subset shared with the backend, see utils/portableRegex.ts)
        if (attributeIsSet(meta.regex)) {
            if (!portableRegexSearch(meta.regex, value)) {
                return false;
            }
        }

        // Check include_values (exact match in list)
        if (isFilledArray(meta.include_values)) {
            if (!meta.include_values.includes(value)) {
                return false;
            }
        }

        // Check exclude_values (must not be in list)
        if (isFilledArray(meta.exclude_values)) {
            if (meta.exclude_values.includes(value)) {
                return false;
            }
        }

        return true;
    });
}

// * base function to filter based on .display_filter props
// * use this directly to filter check locations and layers (root configs)
export function applyDisplayFilter(fields, filter: object | any = {}) {
    if (!fields) {
        return [];
    }

    // * make sure fields is an array
    if (!Array.isArray(fields)) {
        fields = [fields];
    }

    // * Filter for fruit_types, layer_types and manuar / device check
    return fields.filter((i) => {
        // * first check fruit_type
        if (!i.display_filter) {
            return true;
        }

        // * give preference to the including filter (strict allow-list:
        //   requires a positive match, so if fruit_type is missing the
        //   field is hidden — otherwise fields configured for a specific
        //   fruit leak into pallets whose fruit_type is not yet set).
        if (isFilledArray(i.display_filter?.fruit_types)) {
            if (!filter?.fruit_type) {
                return false;
            }
            return i.display_filter.fruit_types.includes(filter.fruit_type);
        }

        // * deny-list is permissive: only hide when fruit_type is set AND
        //   listed as excluded; a missing fruit_type falls through and the
        //   field is shown as the sensible default.
        if (isFilledArray(i.display_filter?.exclude_fruit_types)) {
            if (!filter?.fruit_type) {
                return true;
            }
            return !i.display_filter.exclude_fruit_types.includes(filter.fruit_type);
        }

        return true;

    }).filter((i) => {
        // * then check layer_type
        if (!i.display_filter || !filter?.layer_type) {
            return true;
        }

        // give preference to the including filter
        if (isFilledArray(i.display_filter?.layer_types)) {
            return i.display_filter.layer_types.includes(filter.layer_type);
        }

        // * not using this yet
        // if ((i.display_filter?.exclude_layer_types || []).length > 0) {
        //     return !i.display_filter.layer_types.includes(filter.layer_type);
        // }

        return true;

    }).filter((i) => {
        // * then check is_manual
        if (!i.display_filter) {
            return true;
        }

        if (i.display_filter?.manual_or_with_mini === "only_on_mini_check" && filter?.is_manual === true) {
            return false;
        }

        if (i.display_filter?.manual_or_with_mini === "only_on_manual_check" && filter?.is_manual === false) {
            return false;
        }

        return true;
    }).filter((i) => {
        // * then check facility — only uses filter.facilities (array)
        if (!i.display_filter || !isFilledArray(filter?.facilities)) {
            return true;
        }

        if (isFilledArray(i.display_filter?.facilities)) {
            return i.display_filter.facilities.some((f) => filter.facilities.includes(f));
        }

        if (isFilledArray(i.display_filter?.exclude_facilities)) {
            return !i.display_filter.exclude_facilities.some((f) => filter.facilities.includes(f));
        }

        return true;
    })
        .filter((i) => {
            // * Filter based on meta_conditions in display_filter
            if (!i.display_filter || !i.display_filter?.meta_conditions) {
                return true;
            }

            return matchesMetaConditions(
                i.display_filter.meta_conditions,
                filter.layer || {},
                filter.check || {},
                filter.fruit || {}
            );
        });
}

// * EFQ-3850 — options bag for layer-field setup. Replaces the
// * legacy positional `(fields, filter, fruitTypes, rootDateTimeFormats)`
// * signature so adding new context (e.g. timezones) doesn't keep growing
// * the arg list and silently regress callers that omit a positional.
// * Per-field datetime_format values that match one of these known default
// * formats are treated as "not overridden" (fall through to root config).
// * These strings only ended up in tenant configs via the old hardcoded
// * defaults — no admin UI ever asked users to type them. By treating them
// * as non-overrides we let the root config take effect without needing
// * a data-migration script or DB write.
const KNOWN_DEFAULT_DATE_FORMATS: ReadonlySet<string> = new Set([
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
]);

const KNOWN_DEFAULT_DATETIME_FORMATS: ReadonlySet<string> = new Set([
    "HH:mm DD-MM-YYYY",
    "HH:mm DD/MM/YYYY",
    "HH:mm MM-DD-YYYY",
    "HH:mm MM/DD/YYYY",
    "HH:mm YYYY-MM-DD",
    "DD-MM-YYYY HH:mm",
    "DD/MM/YYYY HH:mm",
    "MM-DD-YYYY HH:mm",
    "MM/DD/YYYY HH:mm",
    "YYYY-MM-DD HH:mm",
]);

export type LayerFieldOptions = {
    fruitTypes?: any[];
    stages?: any[];
    rootDateTimeFormats?: { date_format?: string; datetime_format?: string };
};

export const applyDisplayFilterLayerFields = (
    fields,
    filter: any = {},
    options: LayerFieldOptions = {}
) => {
    return applyDisplayFilterFields(fields, filter).filter((i) => {
        // This never should be the case, but just in case there is an error in the migration script we can use this console log to find out where it the issue comes from
        if (!i?.field_id) {
            console.log(`Error: field_id is undefined?`, i, fields); // eslint-disable-line no-console
            return false; // Remove items with invalid field_id
        }

        if (FIELD[i.field_id]) {
            return true; // Keep items with a valid field_id
        }

        console.log(`Error: Field with id ${i.field_id} does not exist. Trying to show ${i.fieldname_getter}.`); // eslint-disable-line no-console
        return false; // Remove items with invalid field_id
    }).map((field) => setupFieldClassFromConfig(field, filter, options));
};

export const setupFieldClassFromConfig = (
    fieldConfig,
    filter: any = {},
    options: LayerFieldOptions = {}
) => {
    const { fruitTypes = [], stages = [], rootDateTimeFormats } = options;
    if (!fieldConfig?.field_id || !FIELD[fieldConfig?.field_id]) {
        console.log(`Error: Field with id ${fieldConfig.field_id} does not exist. Trying to show ${fieldConfig.fieldname_getter}.`); // eslint-disable-line no-console
        return null;
    }

    let categories = fieldConfig.categories || [];

    if (fieldConfig.inherit_ripening_classes_from_fruit_config) {
        const inherited_categories = fruitTypes
            .filter((fruitType) => filter?.fruit_type && (fruitType.value === filter?.fruit_type))
            .flatMap((fruitType) => getFilledArrayOrDefault(fruitType.ripening_fields).filter((ripeningField) => ripeningField.fieldname_getter === fieldConfig.inherit_ripening_classes_from_fruit_config).flatMap((ripeningField) => getFilledArrayOrDefault(ripeningField.categories).map((option) => ({ ...option, fruit_type: fruitType.value }))));

        // * Only take over the inherited ripening classes when they actually resolved.
        // * The fruit type is derived from loaded data (see useRipeningRoom), so it is
        // * still null on the first render. An unconditional overwrite would replace the
        // * categories with an empty list and the field would render its raw numeric
        // * value instead of the ripening class badge.
        if (isFilledArray(inherited_categories)) {
            categories = inherited_categories;
        }
    }

    // * Never mutate the incoming config: it is the very object held in the tenant config
    // * context and is reused across every render. Emptying its categories here would
    // * permanently break the field, because the next call reads them back as the source.
    const resolved_field_config = { ...fieldConfig, categories };

    const field = FIELD[fieldConfig?.field_id]
        .clone() // * Clone the field to avoid sketchy behavior
        .set_label(fieldConfig.label) // * set the label
        .set_fieldname_getter(fieldConfig.fieldname_getter) // * Set the fieldname for reading the value
        .set_excluded_from_download(fieldConfig.excluded_from_download || false) // * Exclude field from download or not
        .set_option("field_config", resolved_field_config) // * Set the config for the field
        .set_option("stages_config", stages); // * Tenant stages drive backend select_fields for stage-aware badges

    // * Setup the formatter if it is set
    if (fieldConfig.formatter) {
        const { decimals, unity, value_if_null, datetime_format, seperator } = fieldConfig;

        // * Per-field datetime_format is an override; otherwise fall back to
        // * the tenant root format (the default) for the matching formatter.
        // * Empty string is treated as "not overridden".
        // *
        // * Per-field values that match a known default format are also
        // * treated as "not overridden", so the root config takes effect
        // * without a data migration. These values were auto-populated
        // * from hardcoded defaults — they are not intentional overrides.
        const isKnownDefault = (fmt: string): boolean => {
            if (fieldConfig.formatter === "formatDate") return KNOWN_DEFAULT_DATE_FORMATS.has(fmt);
            if (fieldConfig.formatter === "formatDateTime") return KNOWN_DEFAULT_DATETIME_FORMATS.has(fmt);
            return false;
        };
        const explicit_format = (typeof datetime_format === "string" && datetime_format.length > 0 && !isKnownDefault(datetime_format))
            ? datetime_format
            : undefined;
        const effective_datetime_format = (() => {
            if (explicit_format) {
                return explicit_format;
            }
            if (fieldConfig.formatter === "formatDate") {
                return rootDateTimeFormats?.date_format;
            }
            if (fieldConfig.formatter === "formatDateTime") {
                return rootDateTimeFormats?.datetime_format;
            }
            return undefined;
        })();

        //*  Note that we overwrite any previous setup formatters to avoid funny results
        const formatter = setupFormatter(fieldConfig.formatter, { decimals, unity, value_if_null, datetime_format: effective_datetime_format, seperator });
        field.set_option("formatters", [formatter]);
        // * set the filter type for the overview page
        field.set_filter_type_for_formatter(fieldConfig.formatter, { formatter, decimals, unity, value_if_null, datetime_format: effective_datetime_format });
    } else if (field.formatters.length === 0 && looksLikeDateField(fieldConfig.fieldname_getter)) {
        // * Auto-apply formatDate for generic fields (CUSTOM_LAYER_META etc.) whose
        // * fieldname_getter implies a date value. Uses the tenant root date_format.
        const formatter = setupFormatter("formatDate", { decimals: undefined, unity: undefined, value_if_null: fieldConfig.value_if_null, datetime_format: rootDateTimeFormats?.date_format, seperator: undefined });
        field.set_option("formatters", [formatter]);
    }

    if (fieldConfig.options_name) {
        field.set_option("options_name", fieldConfig.options_name);
    }

    return field;
};

// * Shortcut to first apply the filtering based on .display_filter props and then return the field
// * use this for flows, fruit table fields and form definitions etc etc
// * if list of objects doesnt use {display_fiter, field} it will just return the list as is (for backward compability)
export function applyDisplayFilterFields(fields, filter = {}) {
    return applyDisplayFilter(fields, filter).map((i) => {
        const field = i.field || i;

        // * If field has options with display_filter (e.g., fruit-images), filter those too
        if (field?.options && Array.isArray(field.options)) {
            return {
                ...field,
                options: applyDisplayFilter(field.options, filter).map((opt) => opt.field || opt)
            };
        }

        return field;
    });
}

// * Generic function to filter items based on meta_conditions
// * Works with any items that have a meta_conditions property (flags, categories, etc.)
export function applyMetaConditionsFilter(items, layer, check = {}, fruit = {}) {
    if (!items) {
        return [];
    }

    // * make sure items is an array
    if (!Array.isArray(items)) {
        items = [items];
    }

    return items.filter((item) => matchesMetaConditions(item.meta_conditions, layer, check, fruit));
}

// * Backward compatibility aliases - kept for existing code
// * @deprecated Use applyMetaConditionsFilter instead
export function applyTriggerMetaFilter(items, layer, check = {}, fruit = {}) {
    return applyMetaConditionsFilter(items, layer, check, fruit);
}

// * @deprecated Use applyMetaConditionsFilter instead
export function applyTriggerFilterFlags(flags, layer, check = {}, fruit = {}) {
    return applyMetaConditionsFilter(flags, layer, check, fruit);
}
